import React, { useState, useEffect } from "react";
import AnalysisFeedback from "../components/AnalysisFeedback";
import ComplaintAnalysis from "../components/ComplaintAnalysis";
import AdviceAnalysis from "../components/AdviceAnalysis";
import {
  ArrowLeft,
  Building,
  Pill,
  Tag,
  ScrollText,
  FileDown,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";

const AnalysisDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState("summary");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // First, let's modify the logging to be more explicit:
  const fetchAnalysis = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/analyses/${id}`
      );
      if (!response.ok) throw new Error("Failed to fetch analysis");
      const data = await response.json();

      // Add this debug logging
      console.log("Analysis Data Debug:", {
        hasSourceURL: "sourceURL" in data,
        sourceURLValue: data.sourceURL,
        allKeys: Object.keys(data),
        fullData: data,
      });

      setAnalysis(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (id) fetchAnalysis();
  }, [id]);

  const handleDownload = async () => {
    console.log("Download debug:", {
      sourceURL: analysis["sourceURL"],
      fullAnalysis: analysis,
    });

    if (!analysis["sourceURL"]) {
      console.error("No source URL available");
      return;
    }

    try {
      const response = await fetch(analysis["sourceURL"]);
      if (!response.ok) {
        console.error("Download failed:", response.status, response.statusText);
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `CGR-${analysis.zaaknummer}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("nl-NL", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-nn-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nn-500"></div>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-nn-gray-50">
        <div className="max-w-md p-6 bg-white rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            {error ? "Error Loading Analysis" : "Analysis Not Found"}
          </h2>
          <p className="text-nn-gray-600 mb-4">
            {error || "The requested analysis could not be found."}
          </p>
          <button
            onClick={() => navigate("/")}
            className="text-nn-500 hover:text-nn-600 font-medium"
          >
            ← Back to Overview
          </button>
        </div>
      </div>
    );
  }

  const json_output = analysis.json_output || {};
  const documentType = analysis.document_type || "unknown";

  return (
    <div className="min-h-screen bg-nn-gray-50">
      <div className="bg-nn-gray-50">
        <div className="mx-auto">
          <div className="flex flex-col space-y-8">
            <button
              onClick={() => navigate("/")}
              className="flex items-center text-nn-500 hover:text-nn-600 w-fit"
            >
              <ArrowLeft className="h-5 w-5 mr-1" />
              Terug naar overzicht
            </button>

            <div className="flex flex-col space-y-6">
              <div>
                <div className="flex flex-row gap-2 mb-2">
                  {documentType === "klacht" ? (
                    <span className="flex items-center justify-center px-2 py-1 rounded text-sm bg-blue-100 text-blue-800">
                      Klacht {json_output.basis_informatie?.zaaknummer}
                    </span>
                  ) : (
                    <span className="flex items-center justify-center px-2 py-1 rounded text-sm bg-green-100 text-green-800">
                      Advies {json_output.basis_informatie?.zaaknummer}
                    </span>
                  )}
                  <span
                    className="inline-flex items-center p-2 bg-nn-gray-100 text-nn-gray-700 rounded-md text-sm cursor-pointer hover:bg-nn-gray-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/?hoofdcategorie=${encodeURIComponent(
                          json_output.metadata.hoofdcategorie
                        )}`
                      );
                    }}
                  >
                    <Tag className="h-4 w-8 mr-2 text-nn-gray-500" />
                    {json_output.metadata.hoofdcategorie}
                  </span>
                  {analysis?.["sourceURL"] && (
                    <button
                      onClick={handleDownload}
                      className="inline-flex items-center p-2 bg-nn-gray-100 text-nn-gray-700 rounded-md text-sm cursor-pointer hover:bg-nn-gray-200"
                      title="Download origineel document"
                    >
                      <FileDown className="h-4 w-4 text-nn-gray-500" />
                      <span className="ml-2">Download PDF</span>
                    </button>
                  )}
                </div>
                <h1 className="text-2xl font-bold text-nn-500 mb-2">
                  {json_output.basis_informatie?.krantenkop}
                </h1>
                <p className="text-nn-gray-600 mt-2 max-w-2xl">
                  {json_output.onderwerp.essentie}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {documentType === "klacht" && (
                  <div>
                    <h2 className="text-sm font-medium text-nn-gray-500 mb-3">
                      <span className="flex items-center gap-2">
                        <Building className="h-4 w-4" />
                        Betrokken bedrijven
                      </span>
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {json_output.basis_informatie.betrokken_bedrijven.map(
                        (bedrijf, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-nn-50 text-nn-700 rounded text-sm flex items-center gap-1"
                          >
                            {bedrijf}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                )}

                {documentType === "klacht" && (
                  <div>
                    <h2 className="text-sm font-medium text-nn-gray-500 mb-3">
                      <span className="flex items-center gap-2">
                        <Pill className="h-4 w-4" />
                        Geneesmiddelen
                      </span>
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {json_output.basis_informatie.betrokken_geneesmiddelen.map(
                        (med, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-nn-teal-50 text-nn-teal-700 rounded text-sm"
                          >
                            {med}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                )}

                {json_output.metadata?.keywords && (
                  <div>
                    <h2 className="text-sm font-medium text-nn-gray-500 mb-3">
                      <span className="flex items-center gap-2">
                        <Tag className="h-4 w-4" />
                        Onderwerpen
                      </span>
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {json_output.metadata.keywords.map((keyword, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-nn-gray-100 text-nn-gray-700 rounded text-sm"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-6">
              {[
                { id: "summary", label: "Samenvatting" },
                { id: "source", label: "Brontekst" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? "border-nn-500 text-nn-500"
                      : "border-transparent text-nn-gray-500 hover:text-nn-gray-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto py-8">
        {activeTab === "summary" ? (
          <>
            {documentType === "klacht" ? (
              <ComplaintAnalysis data={json_output} formatDate={formatDate} />
            ) : (
              <AdviceAnalysis data={json_output} />
            )}
            <AnalysisFeedback analysisId={id} />
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <ScrollText className="h-5 w-5 text-nn-500" />
                Brontekst
              </h2>
            </div>
            <div className="bg-nn-gray-50 rounded-lg p-6 max-h-[800px] overflow-y-auto">
              <pre className="whitespace-pre-wrap font-sans text-sm text-nn-gray-700">
                {analysis.text || "Geen brontekst beschikbaar"}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisDetail;
